package com.fantasytenis.web.components.common

import androidx.compose.runtime.*
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.svg.SVGElement

private const val SVG_NS = "http://www.w3.org/2000/svg"

/**
 * Material Design style info icon (outlined circle with "i").
 *
 * Features:
 * - SVG rendering for crisp display at any size
 * - Hover state with green tint and scale animation
 * - Active/pressed state with scale down
 * - 44px touch target for accessibility
 *
 * @param onClick Called when the icon is clicked
 * @param size The touch target size (icon will be smaller inside)
 * @param stopPropagation Whether to stop click event propagation
 */
@Composable
fun InfoIcon(
    onClick: () -> Unit,
    size: Int = 44,
    stopPropagation: Boolean = true
) {
    val colors = Theme.colors
    var isHovered by remember { mutableStateOf(false) }
    var isPressed by remember { mutableStateOf(false) }

    // Calculate icon size (smaller than touch target)
    val iconSize = (size * 0.45).toInt().coerceAtLeast(16)

    Div({
        style {
            width(size.px)
            height(size.px)
            minWidth(size.px)
            borderRadius(50.percent)
            backgroundColor(
                when {
                    isPressed -> Color("rgba(0, 166, 81, 0.25)")
                    isHovered -> Color("rgba(0, 166, 81, 0.15)")
                    else -> colors.surfaceVariant  // Light gray background
                }
            )
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            cursor("pointer")
            flexShrink(0)
            property("transition", "all 0.2s ease")
            property("transform", when {
                isPressed -> "scale(0.95)"
                isHovered -> "scale(1.05)"
                else -> "scale(1)"
            })
        }
        onClick { e ->
            if (stopPropagation) {
                e.stopPropagation()
            }
            onClick()
        }
        onMouseEnter { isHovered = true }
        onMouseLeave {
            isHovered = false
            isPressed = false
        }
        onMouseDown { isPressed = true }
        onMouseUp { isPressed = false }
        title("View skill details")
        attr("role", "button")
        attr("aria-label", "View player skill details")
    }) {
        // SVG container
        Div({
            style {
                width(iconSize.px)
                height(iconSize.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
            }
        }) {
            // Use DisposableEffect to render SVG (following RadarChart pattern)
            val iconColor = if (isHovered || isPressed) colors.primary.toString() else colors.textSecondary.toString()

            DisposableEffect(iconColor) {
                val container = scopeElement

                // Clear existing content
                while (container.firstChild != null) {
                    container.removeChild(container.firstChild!!)
                }

                // Create SVG element
                val svg = document.createElementNS(SVG_NS, "svg").apply {
                    setAttribute("width", iconSize.toString())
                    setAttribute("height", iconSize.toString())
                    setAttribute("viewBox", "0 0 24 24")
                    setAttribute("fill", "none")
                    setAttribute("stroke", iconColor)
                    setAttribute("stroke-width", "2")
                    setAttribute("stroke-linecap", "round")
                    setAttribute("stroke-linejoin", "round")
                }

                // Circle outline
                val circle = document.createElementNS(SVG_NS, "circle").apply {
                    setAttribute("cx", "12")
                    setAttribute("cy", "12")
                    setAttribute("r", "10")
                }
                svg.appendChild(circle)

                // The "i" - vertical line
                val line = document.createElementNS(SVG_NS, "line").apply {
                    setAttribute("x1", "12")
                    setAttribute("y1", "16")
                    setAttribute("x2", "12")
                    setAttribute("y2", "12")
                }
                svg.appendChild(line)

                // The "i" - dot
                val dot = document.createElementNS(SVG_NS, "line").apply {
                    setAttribute("x1", "12")
                    setAttribute("y1", "8")
                    setAttribute("x2", "12.01")
                    setAttribute("y2", "8")
                }
                svg.appendChild(dot)

                container.appendChild(svg)

                onDispose {
                    while (container.firstChild != null) {
                        container.removeChild(container.firstChild!!)
                    }
                }
            }
        }
    }
}
